
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';
import { createArticle, updateArticle, Article } from '../../../utils/articleApi';
import { ARTICLE_FORM_CATEGORY_OPTIONS } from '../../../utils/articleCategories';
import { defaultArticleStyles, getCustomStyles, stylesToCSS, ArticleStyles } from '../../../styles/articleContentStyles';
import { stripArticleBoilerplate } from '../../../utils/stripArticleBoilerplate';
import { ARTICLE_MID_CTA_MARKER, splitBodyAtMidCta } from '../../../utils/articleMidCta';
import { insertHtmlSnippet, insertPlainSnippet, type EditorCaret } from '../../../utils/insertEditorSnippet';

const DEFAULT_ARTICLE_AUTHOR = 'SOCIAL TRIP編集部';
const DEFAULT_READ_TIME = '10分';

interface ArticleEditorProps {
  article?: Article | null;
  onSave: () => void;
  onCancel: () => void;
}

export default function ArticleEditor({ article, onSave, onCancel }: ArticleEditorProps) {
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    selectedCategories: [] as string[],
    tags: '',
    author: DEFAULT_ARTICLE_AUTHOR,
    readTime: DEFAULT_READ_TIME,
    layout: 'standard' as 'standard' | 'featured',
    publishedAt: new Date().toISOString().split('T')[0],
    imageUrl: ''
  });

  const [activeTab, setActiveTab] = useState<'basic' | 'content' | 'preview' | 'css'>('basic');
  const [htmlContent, setHtmlContent] = useState('');
  const [customStyles, setCustomStyles] = useState<ArticleStyles>(defaultArticleStyles);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cloudinaryInfo, setCloudinaryInfo] = useState<{
    configured: boolean;
    baseUrl: string;
    exampleUrl: string;
  }>({
    configured: false,
    baseUrl: '',
    exampleUrl: 'https://example.com/images/your-image.jpg'
  });

  // 概要・抜粋用の画像挿入モーダル
  const [showExcerptImageModal, setShowExcerptImageModal] = useState(false);
  const [excerptImageUploading, setExcerptImageUploading] = useState(false);
  const [excerptImageDragging, setExcerptImageDragging] = useState(false);
  const [showExcerptPreview, setShowExcerptPreview] = useState(false);

  // HTMLコンテンツ用の画像挿入モーダル
  const [showHtmlImageModal, setShowHtmlImageModal] = useState(false);
  const [htmlImageUploading, setHtmlImageUploading] = useState(false);
  const [htmlImageDragging, setHtmlImageDragging] = useState(false);
  const [htmlImageNotice, setHtmlImageNotice] = useState('');
  const [excerptImageNotice, setExcerptImageNotice] = useState('');
  const htmlTextareaRef = useRef<HTMLTextAreaElement>(null);
  const excerptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const htmlContentRef = useRef(htmlContent);
  htmlContentRef.current = htmlContent;
  const excerptValueRef = useRef(formData.excerpt);
  excerptValueRef.current = formData.excerpt;
  const htmlCaretRef = useRef<EditorCaret>({ start: 0, end: 0, touched: false });
  const excerptCaretRef = useRef<EditorCaret>({ start: 0, end: 0, touched: false });

  const rememberHtmlCaret = (el: HTMLTextAreaElement) => {
    htmlCaretRef.current = { start: el.selectionStart, end: el.selectionEnd, touched: true };
  };

  const rememberExcerptCaret = (el: HTMLTextAreaElement) => {
    excerptCaretRef.current = { start: el.selectionStart, end: el.selectionEnd, touched: true };
  };

  const focusTextareaAt = (el: HTMLTextAreaElement | null, caret: number) => {
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  // カスタムスタイルをローカルストレージから読み込む
  useEffect(() => {
    const loaded = getCustomStyles();
    if (loaded) {
      setCustomStyles(loaded);
    }
  }, []);

  // Cloudinary情報を取得
  useEffect(() => {
    const fetchCloudinaryInfo = async () => {
      try {
        const response = await fetch('/.netlify/functions/cloudinary-info', {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setCloudinaryInfo(data);
        }
      } catch (error) {
        console.error('Cloudinary情報の取得に失敗:', error);
      }
    };
    fetchCloudinaryInfo();
  }, []);

  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title,
        excerpt: article.excerpt,
        content: article.content || '',
        selectedCategories: article.categories?.length
          ? [...article.categories]
          : article.category
            ? article.category
                .split(/[・,、]/)
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        tags: article.tags.join(', '),
        author: article.author,
        readTime: article.readTime,
        layout: article.layout || 'standard',
        publishedAt: article.publishedAt,
        imageUrl: article.image || ''
      });
      setHtmlContent(article.content || '');
      setPreviewImage(article.image || '');
      htmlCaretRef.current = { start: 0, end: 0, touched: false };
      excerptCaretRef.current = { start: 0, end: 0, touched: false };
      setHtmlImageNotice('');
      setExcerptImageNotice('');
    } else {
      // 新規記事の場合、画像URLは空欄・著者・読了時間はデフォルト
      setFormData((prev) => ({
        ...prev,
        imageUrl: '',
        selectedCategories: [],
        author: DEFAULT_ARTICLE_AUTHOR,
        readTime: DEFAULT_READ_TIME,
      }));
      setPreviewImage('');
    }
  }, [article]);

  // レイアウトが変更されても画像URLは変更しない（ユーザーが明示的に設定した値を保持）

  // 画像URL入力フィールドの変更をプレビューに反映
  useEffect(() => {
    if (formData.imageUrl) {
      setPreviewImage(formData.imageUrl);
    }
  }, [formData.imageUrl]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleCategory = (value: string) => {
    setFormData((prev) => {
      const set = new Set(prev.selectedCategories);
      if (set.has(value)) {
        set.delete(value);
      } else {
        set.add(value);
      }
      return { ...prev, selectedCategories: Array.from(set) };
    });
  };

  const handleHtmlContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value;
    setHtmlContent(content);
    // リアルタイムでformDataも更新
    setFormData(prev => ({ ...prev, content }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.selectedCategories.length === 0) {
      alert('カテゴリを1つ以上選択してください。');
      return;
    }

    // HTMLコンテンツをクリーンアップ
    const cleanedContent = cleanHtmlContent(htmlContent);

    const categories = formData.selectedCategories;
    const category = categories.join('・');

    const articleData = {
      title: formData.title,
      excerpt: formData.excerpt,
      content: cleanedContent,
      category,
      categories,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      author: formData.author || DEFAULT_ARTICLE_AUTHOR,
      readTime: formData.readTime || DEFAULT_READ_TIME,
      layout: formData.layout,
      publishedAt: formData.publishedAt,
      publishDate: new Date(formData.publishedAt).toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      status: '公開',
      image: formData.imageUrl || getImageUrlFromLayout(formData.layout)
    };

    try {
      if (article) {
        // 既存記事の更新
        const success = await updateArticle(article.id, articleData);
        if (success) {
          alert('記事が正常に更新されました！');
          onSave();
        } else {
          alert('記事の更新に失敗しました。');
        }
      } else {
        // 新規記事の追加
        await createArticle(articleData);
        alert('記事が正常に作成されました！');
        onSave();
      }
    } catch (error) {
      console.error('保存エラー:', error);
      alert('記事の保存中にエラーが発生しました。');
    }
  };

  const cleanHtmlContent = (html: string) => {
    // 危険なタグとスタイルを除去
    let cleaned = html
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<style[^>]*>.*?<\/style>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '') // イベントハンドラーを除去
      .replace(/\javascript:/gi, '') // javascript:プロトコルを除去
      .replace(/\sclass="[^"]*"/gi, '') // class属性を除去
      .replace(/\sstyle="[^"]*"/gi, '') // style属性を除去
      .replace(/\s+/g, ' ')
      .trim();

    return cleaned;
  };

  const getImageUrlFromLayout = (layout: string) => {
    const width = layout === 'featured' ? 800 : 600;
    const height = layout === 'featured' ? 600 : 400;
    return `https://readdy.ai/api/search-image?query=beautiful travel destination scenic landscape with vibrant colors and peaceful atmosphere perfect for travel blog article&width=${width}&height=${height}&seq=${Date.now()}&orientation=landscape`;
  };

  const generateAIPrompt = () => {
    const prompt = `以下の情報を元に、旅行ブログ記事のHTMLコンテンツを生成してください：

タイトル: ${formData.title}
カテゴリ: ${formData.selectedCategories.join('、')}
概要: ${formData.excerpt}

要件:
- **HTML構造のみ**を出力してください（classやstyle属性は一切不要）
- 見出し（h2, h3）、段落（p）、リスト（ul, ol）を適切に使用
- 画像は <img src="https://readdy.ai/api/search-image?query=[画像の説明]&width=800&height=400&seq=[ユニークID]&orientation=landscape" alt="[説明]"> の形式で挿入（class属性は不要）
- 重要な部分は <strong> や <em> で強調
- スタイル（CSS）はシステム側で自動適用されるため、classやstyle属性は記述しないでください
- 読みやすい構造で、約${formData.readTime}の内容量

生成されたHTMLをそのままコピーして貼り付けてください。`;

    navigator.clipboard.writeText(prompt).then(() => {
      alert('生成AI用のプロンプトをクリップボードにコピーしました！');
    }).catch(() => {
      // フォールバック：テキストエリアを作成してコピー
      const textArea = document.createElement('textarea');
      textArea.value = prompt;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('生成AI用のプロンプトをクリップボードにコピーしました！');
    });
  };

  const cleanHtml = () => {
    const cleaned = cleanHtmlContent(htmlContent);
    setHtmlContent(cleaned);
    setFormData(prev => ({ ...prev, content: cleaned }));
    alert('HTMLをクリーンアップしました！');
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 画像ファイルのみ許可
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください。');
      return;
    }

    // ファイルサイズチェック（5MB以下）
    if (file.size > 5 * 1024 * 1024) {
      alert('ファイルサイズが大きすぎます。5MB以下の画像を選択してください。');
      return;
    }

    setUploading(true);

    try {
      // ファイルをbase64に変換
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageData = event.target?.result as string;

        // ファイル名を生成（タイムスタンプ + オリジナルファイル名）
        const timestamp = Date.now();
        const fileName = `${timestamp}-${file.name}`;

        try {
          // Netlify Functionを使ってGitHubに画像をアップロード
          const response = await fetch('/.netlify/functions/upload-image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              imageData,
              fileName,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`アップロードエラー: ${errorData.message || response.statusText}`);
          }

          const result = await response.json();

          // アップロードされた画像のURLをフォームに設定
          setFormData(prev => ({ ...prev, imageUrl: result.imageUrl }));
          setPreviewImage(result.imageUrl);

          alert('画像をCloudinaryにアップロードしました！');
        } catch (error: any) {
          console.error('画像アップロードエラー:', error);
          alert(`画像のアップロードに失敗しました: ${error.message}`);
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        alert('ファイルの読み込みに失敗しました。');
        setUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('画像処理エラー:', error);
      alert('画像の処理中にエラーが発生しました。');
      setUploading(false);
    }
  };

  // ドラッグ&ドロップ用のハンドラー
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];

    // 画像ファイルのみ許可
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルをドロップしてください。');
      return;
    }

    // ファイルサイズチェック（5MB以下）
    if (file.size > 5 * 1024 * 1024) {
      alert('ファイルサイズが大きすぎます。5MB以下の画像を選択してください。');
      return;
    }

    setUploading(true);

    try {
      // ファイルをbase64に変換
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageData = event.target?.result as string;

        // ファイル名を生成（タイムスタンプ + オリジナルファイル名）
        const timestamp = Date.now();
        const fileName = `${timestamp}-${file.name}`;

        try {
          // Netlify Functionを使ってCloudinaryに画像をアップロード
          const response = await fetch('/.netlify/functions/upload-image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              imageData,
              fileName,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`アップロードエラー: ${errorData.message || response.statusText}`);
          }

          const result = await response.json();

          // アップロードされた画像のURLをフォームに設定
          setFormData(prev => ({ ...prev, imageUrl: result.imageUrl }));
          setPreviewImage(result.imageUrl);

          alert('画像をCloudinaryにアップロードしました！');
        } catch (error: any) {
          console.error('画像アップロードエラー:', error);
          alert(`画像のアップロードに失敗しました: ${error.message}`);
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        alert('ファイルの読み込みに失敗しました。');
        setUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('画像処理エラー:', error);
      alert('画像の処理中にエラーが発生しました。');
      setUploading(false);
    }
  };

  // HTMLコンテンツ用の画像アップロード処理
  const handleHtmlImageUpload = async (file: File) => {
    // 画像ファイルのみ許可
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください。');
      return;
    }

    // ファイルサイズチェック（5MB以下）
    if (file.size > 5 * 1024 * 1024) {
      alert('ファイルサイズが大きすぎます。5MB以下の画像を選択してください。');
      return;
    }

    setHtmlImageUploading(true);

    try {
      // ファイルをbase64に変換
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageData = event.target?.result as string;

        // ファイル名を生成（タイムスタンプ + オリジナルファイル名）
        const timestamp = Date.now();
        const fileName = `${timestamp}-${file.name}`;

        try {
          // Netlify Functionを使ってCloudinaryに画像をアップロード
          const response = await fetch('/.netlify/functions/upload-image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              imageData,
              fileName,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`アップロードエラー: ${errorData.message || response.statusText}`);
          }

          const result = await response.json();

          const imgTag = `<img src="${result.imageUrl}" alt="画像の説明">`;
          const snippet = `\n${imgTag}\n`;
          const { next, caret } = insertHtmlSnippet(htmlContentRef.current, snippet, htmlCaretRef.current);
          htmlContentRef.current = next;
          htmlCaretRef.current = { start: caret, end: caret, touched: true };
          setHtmlContent(next);
          setFormData((prev) => ({ ...prev, content: next }));
          setShowHtmlImageModal(false);
          setActiveTab('content');
          setHtmlImageNotice('画像を本文に挿入しました。位置を確認してから保存してください。');
          focusTextareaAt(htmlTextareaRef.current, caret);
        } catch (error: any) {
          console.error('画像アップロードエラー:', error);
          alert(`画像のアップロードに失敗しました: ${error.message}`);
        } finally {
          setHtmlImageUploading(false);
        }
      };

      reader.onerror = () => {
        alert('ファイルの読み込みに失敗しました。');
        setHtmlImageUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('画像処理エラー:', error);
      alert('画像の処理中にエラーが発生しました。');
      setHtmlImageUploading(false);
    }
  };

  // 概要・抜粋用の画像アップロード処理
  const handleExcerptImageUpload = async (file: File) => {
    // 画像ファイルのみ許可
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください。');
      return;
    }

    // ファイルサイズチェック（5MB以下）
    if (file.size > 5 * 1024 * 1024) {
      alert('ファイルサイズが大きすぎます。5MB以下の画像を選択してください。');
      return;
    }

    setExcerptImageUploading(true);

    try {
      // ファイルをbase64に変換
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageData = event.target?.result as string;

        // ファイル名を生成（タイムスタンプ + オリジナルファイル名）
        const timestamp = Date.now();
        const fileName = `${timestamp}-${file.name}`;

        try {
          // Netlify Functionを使ってCloudinaryに画像をアップロード
          const response = await fetch('/.netlify/functions/upload-image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              imageData,
              fileName,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`アップロードエラー: ${errorData.message || response.statusText}`);
          }

          const result = await response.json();

          const markdownImage = `![画像の説明](${result.imageUrl})`;
          const { next, caret } = insertPlainSnippet(
            excerptValueRef.current,
            markdownImage,
            excerptCaretRef.current,
          );
          excerptValueRef.current = next;
          excerptCaretRef.current = { start: caret, end: caret, touched: true };
          setFormData((prev) => ({ ...prev, excerpt: next }));
          setShowExcerptImageModal(false);
          setExcerptImageNotice('画像記法を概要欄に挿入しました。公開ページの概要はテキスト表示です。本文中の画像は「HTMLコンテンツ」タブから入れてください。');
          void navigator.clipboard.writeText(markdownImage).catch(() => {});
          focusTextareaAt(excerptTextareaRef.current, caret);
        } catch (error: any) {
          console.error('画像アップロードエラー:', error);
          alert(`画像のアップロードに失敗しました: ${error.message}`);
        } finally {
          setExcerptImageUploading(false);
        }
      };

      reader.onerror = () => {
        alert('ファイルの読み込みに失敗しました。');
        setExcerptImageUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('画像処理エラー:', error);
      alert('画像の処理中にエラーが発生しました。');
      setExcerptImageUploading(false);
    }
  };

  // 概要・抜粋用のドラッグ&ドロップハンドラー
  const handleExcerptImageDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExcerptImageDragging(true);
  };

  const handleExcerptImageDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExcerptImageDragging(false);
  };

  const handleExcerptImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleExcerptImageDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExcerptImageDragging(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    await handleExcerptImageUpload(file);
  };

  const handleExcerptImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleExcerptImageUpload(file);
  };

  // HTMLコンテンツ用のドラッグ&ドロップハンドラー
  const handleHtmlImageDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHtmlImageDragging(true);
  };

  const handleHtmlImageDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHtmlImageDragging(false);
  };

  const handleHtmlImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleHtmlImageDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHtmlImageDragging(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    await handleHtmlImageUpload(file);
  };

  const handleHtmlImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleHtmlImageUpload(file);
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {article ? '記事を編集' : '新しい記事を作成'}
        </h2>
        <p className="text-gray-600">
          生成AIで作成したHTMLコンテンツを貼り付けて、効率的に記事を作成できます
        </p>
      </div>

      {/* タブナビゲーション */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        <div className="flex min-w-full md:min-w-0">
          <button
            type="button"
            onClick={() => setActiveTab('basic')}
            className={`px-3 md:px-4 py-2 font-medium text-xs md:text-sm border-b-2 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0 ${
              activeTab === 'basic'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            基本情報
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('content')}
            className={`px-3 md:px-4 py-2 font-medium text-xs md:text-sm border-b-2 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0 ${
              activeTab === 'content'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            HTMLコンテンツ
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('preview')}
            className={`px-3 md:px-4 py-2 font-medium text-xs md:text-sm border-b-2 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0 ${
              activeTab === 'preview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            プレビュー
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('css')}
            className={`px-3 md:px-4 py-2 font-medium text-xs md:text-sm border-b-2 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0 ${
              activeTab === 'css'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            CSS編集
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* 基本情報タブ */}
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  タイトル *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  カテゴリ *（複数選択可）
                </label>
                <div className="flex flex-wrap gap-3 rounded-md border border-gray-300 p-3 bg-gray-50/80">
                  {ARTICLE_FORM_CATEGORY_OPTIONS.map((opt) => (
                    <label
                      key={opt}
                      className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-800"
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedCategories.includes(opt)}
                        onChange={() => toggleCategory(opt)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">1つ以上選択してください</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  著者
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  読了時間
                </label>
                <input
                  type="text"
                  name="readTime"
                  value={formData.readTime}
                  onChange={handleInputChange}
                  placeholder={`例: ${DEFAULT_READ_TIME}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  レイアウト
                </label>
                <select
                  name="layout"
                  value={formData.layout}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                >
                  <option value="standard">標準</option>
                  <option value="featured">特集</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  公開日
                </label>
                <input
                  type="date"
                  name="publishedAt"
                  value={formData.publishedAt}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  概要・抜粋 *
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const el = excerptTextareaRef.current;
                      if (el) rememberExcerptCaret(el);
                      setShowExcerptImageModal(true);
                    }}
                    className="text-xs whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className="ri-image-add-line mr-1"></i>
                    画像を挿入
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowExcerptPreview(!showExcerptPreview)}
                    className="text-xs whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className={`ri-eye-${showExcerptPreview ? 'off' : ''}line mr-1`}></i>
                    {showExcerptPreview ? 'プレビューを隠す' : 'プレビュー'}
                  </Button>
                </div>
              </div>
              <textarea
                ref={excerptTextareaRef}
                name="excerpt"
                value={formData.excerpt}
                onChange={(e) => {
                  rememberExcerptCaret(e.currentTarget);
                  handleInputChange(e);
                }}
                onSelect={(e) => rememberExcerptCaret(e.currentTarget)}
                onClick={(e) => rememberExcerptCaret(e.currentTarget)}
                onKeyUp={(e) => rememberExcerptCaret(e.currentTarget)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="記事の概要を入力してください。画像を挿入する場合は ![画像の説明](画像URL) の形式で記述します。"
                required
              />
              {excerptImageNotice && (
                <p className="mt-2 text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
                  {excerptImageNotice}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                <i className="ri-information-line mr-1"></i>
                マークダウン記法に対応しています。画像: <code className="bg-gray-100 px-1 rounded">![説明](URL)</code>、太字: <code className="bg-gray-100 px-1 rounded">**テキスト**</code>、リンク: <code className="bg-gray-100 px-1 rounded">[テキスト](URL)</code>
              </p>

              {showExcerptPreview && (
                <div className="mt-3 p-4 border border-gray-200 rounded-md bg-gray-50">
                  <div className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <i className="ri-eye-line mr-1"></i>
                    プレビュー
                  </div>
                  <div className="prose prose-sm max-w-none bg-white p-3 rounded">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {formData.excerpt}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                タグ（カンマ区切り）
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="例: 旅行, グルメ, 温泉"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="font-medium text-gray-900 mb-3">
                <i className="ri-image-line mr-2"></i>
                記事のメイン画像
              </h3>

              <div className="space-y-4">
                {/* ドラッグ&ドロップゾーン */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    画像ファイルをアップロード
                  </label>
                  <div
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                      isDragging
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 bg-white hover:border-gray-400'
                    } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                      id="image-upload"
                    />
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-3"></i>
                        <p className="text-sm font-medium text-blue-600">
                          Cloudinaryにアップロード中...
                        </p>
                      </div>
                    ) : isDragging ? (
                      <div className="flex flex-col items-center">
                        <i className="ri-download-cloud-line text-4xl text-blue-600 mb-3"></i>
                        <p className="text-sm font-medium text-blue-600">
                          ここにドロップしてアップロード
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-3"></i>
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          画像をドラッグ&ドロップ
                        </p>
                        <p className="text-xs text-gray-500 mb-2">
                          または
                        </p>
                        <label
                          htmlFor="image-upload"
                          className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 inline-block cursor-pointer"
                        >
                          <i className="ri-folder-open-line mr-2"></i>
                          ファイルを選択
                        </label>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    JPG、PNG、GIF形式（5MB以下）。アップロードするとCloudinaryに保存されます。
                  </p>
                </div>

                {/* 画像URL手動入力（オプション） */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    または画像URLを入力
                  </label>
                  <input
                    type="text"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleInputChange}
                    placeholder={cloudinaryInfo.configured
                      ? `例: ${cloudinaryInfo.exampleUrl}`
                      : "例: https://example.com/image.jpg"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {cloudinaryInfo.configured
                      ? `Cloudinary (${cloudinaryInfo.baseUrl}) または外部URLを入力できます。`
                      : "外部URLを直接入力することもできます。"}
                  </p>
                </div>

                {previewImage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      画像プレビュー
                    </label>
                    <img
                      src={previewImage}
                      alt="プレビュー"
                      className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/600x400?text=画像の読み込みに失敗しました';
                      }}
                    />
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const newImage = getImageUrlFromLayout(formData.layout);
                      setFormData(prev => ({ ...prev, imageUrl: newImage }));
                      setPreviewImage(newImage);
                    }}
                    className="text-sm whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    AI画像を生成
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, imageUrl: '' }));
                      setPreviewImage('');
                    }}
                    className="text-sm whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className="ri-delete-bin-line mr-2"></i>
                    画像をクリア
                  </Button>
                </div>
              </div>
            </div>

            {/* 生成AI活用ガイド */}
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <h3 className="font-medium text-blue-900 mb-2">
                <i className="ri-lightbulb-line mr-2"></i>
                生成AI活用ガイド
              </h3>
              <div className="text-sm text-blue-700 mb-3 space-y-2">
                <p className="font-medium">HTMLコンテンツを生成AIで作成する手順：</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>上記の基本情報（タイトル、カテゴリ、概要など）を入力</li>
                  <li>下のボタンをクリックして、生成AI用プロンプトをコピー</li>
                  <li><strong>ChatGPTやClaudeなどの生成AIにプロンプトを貼り付け</strong></li>
                  <li><strong>生成AIが出力したHTMLコード（のみ）をコピー</strong></li>
                  <li>「HTMLコンテンツ」タブを開き、エディタに<strong>HTMLコードだけ</strong>を貼り付け</li>
                </ol>
                <p className="text-xs text-blue-600 mt-2">
                  ⚠️ 注意：プロンプト自体ではなく、生成AIが出力した<strong>HTMLコードのみ</strong>を「HTMLコンテンツ」タブに貼り付けてください。
                </p>
              </div>
              <Button
                type="button"
                variant="primary"
                onClick={generateAIPrompt}
                className="cursor-pointer"
              >
                <i className="ri-file-copy-line mr-2"></i>
                生成AI用プロンプトをコピー
              </Button>
            </div>
          </div>
        )}

        {/* HTMLコンテンツタブ */}
        {activeTab === 'content' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h3 className="text-lg font-medium text-gray-900">HTMLコンテンツエディタ</h3>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const el = htmlTextareaRef.current;
                    if (el) rememberHtmlCaret(el);
                    setHtmlImageNotice('');
                    setShowHtmlImageModal(true);
                  }}
                  className="text-xs whitespace-nowrap cursor-pointer w-full sm:w-auto"
                >
                  <i className="ri-image-add-line mr-1"></i>
                  画像を挿入
                </Button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    cleanHtml();
                  }}
                  className="px-4 py-2 text-sm font-medium text-[#02D0F0] border border-[#02D0F0] rounded-xl hover:bg-[#02D0F0] hover:text-white transition-all cursor-pointer w-full sm:w-auto"
                >
                  <i className="ri-brush-line mr-2"></i>
                  HTMLクリーンアップ
                </button>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                <strong>使い方：</strong> 生成AIで作成されたHTMLコンテンツをそのまま貼り付けてください。
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• 不要なスタイルやスクリプトは自動で除去されます</li>
                <li>• HTML構造のみを記述してください（classやstyle属性は不要）</li>
                <li>• スタイルは「CSS編集」タブで一括管理されます</li>
                <li>• 「画像を挿入」でアップロードすると、カーソル位置（未指定なら本文末尾）に img タグが入ります</li>
              </ul>
            </div>

            <div className="bg-teal-50 border border-teal-200 p-4 rounded-lg">
              <p className="text-sm font-medium text-teal-900 mb-1">中盤に App Store 誘導（CV）を置く場合</p>
              <p className="text-xs text-teal-800 mb-2">
                本文 HTML の<strong>希望する位置</strong>に、次の1行をそのまま貼り付けてください（公開ページでのみ有効・UTM は中盤用に自動付与されます）。
              </p>
              <code className="block text-xs bg-white border border-teal-100 rounded px-2 py-1.5 font-mono text-teal-900 break-all">
                {ARTICLE_MID_CTA_MARKER}
              </code>
            </div>

            <textarea
              ref={htmlTextareaRef}
              value={htmlContent}
              onChange={(e) => {
                rememberHtmlCaret(e.currentTarget);
                handleHtmlContentChange(e);
              }}
              onSelect={(e) => rememberHtmlCaret(e.currentTarget)}
              onClick={(e) => rememberHtmlCaret(e.currentTarget)}
              onKeyUp={(e) => rememberHtmlCaret(e.currentTarget)}
              rows={20}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="生成AIで作成されたHTMLコンテンツをここに貼り付けてください..."
            />
            {htmlImageNotice && (
              <p className="text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
                {htmlImageNotice}
              </p>
            )}

            <div className="text-sm text-gray-500">
              文字数: {htmlContent.length} 文字
            </div>
          </div>
        )}

        {/* プレビュータブ */}
        {activeTab === 'preview' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <h3 className="text-lg font-medium text-gray-900">プレビュー</h3>
              {customStyles && (
                <span className="text-xs text-green-600">
                  <i className="ri-check-line"></i> カスタムスタイル適用中
                </span>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg p-6 bg-white">
              {/* メイン画像 */}
              {previewImage && (
                <img
                  src={previewImage}
                  alt={formData.title || 'プレビュー画像'}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}

              <div className="mb-4">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{formData.title || 'タイトルを入力してください'}</h1>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span>{formData.publishedAt}</span>
                  <span>•</span>
                  <span>
                    {formData.selectedCategories.length > 0
                      ? formData.selectedCategories.join('・')
                      : '—'}
                  </span>
                  <span>•</span>
                  <span>{formData.readTime}</span>
                </div>
                <p className="text-lg text-gray-600 mb-6">{formData.excerpt}</p>
              </div>

              <style dangerouslySetInnerHTML={{ __html: stylesToCSS(customStyles) }} />
              {(() => {
                const previewBody = stripArticleBoilerplate(htmlContent);
                const { before, after, hasMid } = splitBodyAtMidCta(previewBody);
                if (hasMid) {
                  return (
                    <>
                      <div className="article-content" dangerouslySetInnerHTML={{ __html: before }} />
                      <div className="my-6 rounded-lg border-2 border-dashed border-teal-400 bg-teal-50 px-4 py-3 text-center text-sm text-teal-900">
                        中盤 App Store 誘導ブロック（公開ページで表示・UTM: utm_medium=article_mid）
                      </div>
                      <div className="article-content" dangerouslySetInnerHTML={{ __html: after }} />
                    </>
                  );
                }
                return (
                  <div
                    className="article-content"
                    dangerouslySetInnerHTML={{ __html: previewBody }}
                  />
                );
              })()}
            </div>
          </div>
        )}

        {/* CSS編集タブ */}
        {activeTab === 'css' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h3 className="text-lg font-medium text-gray-900">CSSスタイル編集</h3>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCustomStyles(defaultArticleStyles);
                    localStorage.removeItem('customArticleStyles');
                    alert('デフォルトスタイルにリセットしました');
                  }}
                  className="text-sm whitespace-nowrap cursor-pointer w-full sm:w-auto"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  デフォルトに戻す
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    localStorage.setItem('customArticleStyles', JSON.stringify(customStyles));
                    alert('CSSスタイルを保存しました！プレビュータブで確認できます。');
                  }}
                  className="text-sm whitespace-nowrap cursor-pointer w-full sm:w-auto"
                >
                  <i className="ri-save-line mr-2"></i>
                  スタイルを保存
                </Button>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700 mb-2">
                <strong>使い方：</strong> 各HTML要素のCSSプロパティを編集できます。SOCIAL TRIPブランド仕様に準拠したデフォルト値が設定されています。
              </p>
              <p className="text-xs text-blue-600">
                例: フォントサイズ「2rem」、色「#333333」、余白「1rem」、グラデーション「linear-gradient(...)」など。
              </p>
            </div>

            {/* 見出し H1 */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">見出し1 (H1) - グラデーションテキスト + 下線アクセント</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントサイズ</label>
                  <input
                    type="text"
                    value={customStyles.h1.fontSize}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, fontSize: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 2.25rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色（グラデーション対応）</label>
                  <input
                    type="text"
                    value={customStyles.h1.color}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: linear-gradient(...)"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントファミリー</label>
                  <input
                    type="text"
                    value={customStyles.h1.fontFamily}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, fontFamily: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: Noto Sans JP, sans-serif"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントウェイト</label>
                  <input
                    type="text"
                    value={customStyles.h1.fontWeight}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, fontWeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 700"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">行の高さ</label>
                  <input
                    type="text"
                    value={customStyles.h1.lineHeight}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, lineHeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1.4"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">字間</label>
                  <input
                    type="text"
                    value={customStyles.h1.letterSpacing}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, letterSpacing: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 0.5px"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">上余白</label>
                  <input
                    type="text"
                    value={customStyles.h1.marginTop}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, marginTop: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 2.5rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下余白</label>
                  <input
                    type="text"
                    value={customStyles.h1.marginBottom}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, marginBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1.5rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下パディング</label>
                  <input
                    type="text"
                    value={customStyles.h1.paddingBottom}
                    onChange={(e) => setCustomStyles({...customStyles, h1: {...customStyles.h1, paddingBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1rem"
                  />
                </div>
              </div>
            </div>

            {/* 見出し H2 */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">見出し2 (H2) - 左アクセントライン + 背景</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントサイズ</label>
                  <input
                    type="text"
                    value={customStyles.h2.fontSize}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, fontSize: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1.875rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色</label>
                  <input
                    type="text"
                    value={customStyles.h2.color}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: #111827"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントファミリー</label>
                  <input
                    type="text"
                    value={customStyles.h2.fontFamily}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, fontFamily: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントウェイト</label>
                  <input
                    type="text"
                    value={customStyles.h2.fontWeight}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, fontWeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">行の高さ</label>
                  <input
                    type="text"
                    value={customStyles.h2.lineHeight}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, lineHeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">字間</label>
                  <input
                    type="text"
                    value={customStyles.h2.letterSpacing}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, letterSpacing: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">上余白</label>
                  <input
                    type="text"
                    value={customStyles.h2.marginTop}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, marginTop: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 2rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下余白</label>
                  <input
                    type="text"
                    value={customStyles.h2.marginBottom}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, marginBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1.25rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">パディング</label>
                  <input
                    type="text"
                    value={customStyles.h2.padding}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, padding: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1rem 1.5rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">背景</label>
                  <input
                    type="text"
                    value={customStyles.h2.background}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, background: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: linear-gradient(...)"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">角丸</label>
                  <input
                    type="text"
                    value={customStyles.h2.borderRadius}
                    onChange={(e) => setCustomStyles({...customStyles, h2: {...customStyles.h2, borderRadius: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 0 8px 8px 0"
                  />
                </div>
              </div>
            </div>

            {/* 見出し H3 */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">見出し3 (H3) - 下線アクセント + アイコン風ドット</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントサイズ</label>
                  <input
                    type="text"
                    value={customStyles.h3.fontSize}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, fontSize: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1.5rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色</label>
                  <input
                    type="text"
                    value={customStyles.h3.color}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントファミリー</label>
                  <input
                    type="text"
                    value={customStyles.h3.fontFamily}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, fontFamily: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントウェイト</label>
                  <input
                    type="text"
                    value={customStyles.h3.fontWeight}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, fontWeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">行の高さ</label>
                  <input
                    type="text"
                    value={customStyles.h3.lineHeight}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, lineHeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">字間</label>
                  <input
                    type="text"
                    value={customStyles.h3.letterSpacing}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, letterSpacing: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">上余白</label>
                  <input
                    type="text"
                    value={customStyles.h3.marginTop}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, marginTop: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下余白</label>
                  <input
                    type="text"
                    value={customStyles.h3.marginBottom}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, marginBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下パディング</label>
                  <input
                    type="text"
                    value={customStyles.h3.paddingBottom}
                    onChange={(e) => setCustomStyles({...customStyles, h3: {...customStyles.h3, paddingBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* 段落 P */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">段落 (P)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントサイズ</label>
                  <input
                    type="text"
                    value={customStyles.p.fontSize}
                    onChange={(e) => setCustomStyles({...customStyles, p: {...customStyles.p, fontSize: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色</label>
                  <input
                    type="text"
                    value={customStyles.p.color}
                    onChange={(e) => setCustomStyles({...customStyles, p: {...customStyles.p, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントファミリー</label>
                  <input
                    type="text"
                    value={customStyles.p.fontFamily}
                    onChange={(e) => setCustomStyles({...customStyles, p: {...customStyles.p, fontFamily: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントウェイト</label>
                  <input
                    type="text"
                    value={customStyles.p.fontWeight}
                    onChange={(e) => setCustomStyles({...customStyles, p: {...customStyles.p, fontWeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">行の高さ</label>
                  <input
                    type="text"
                    value={customStyles.p.lineHeight}
                    onChange={(e) => setCustomStyles({...customStyles, p: {...customStyles.p, lineHeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下余白</label>
                  <input
                    type="text"
                    value={customStyles.p.marginBottom}
                    onChange={(e) => setCustomStyles({...customStyles, p: {...customStyles.p, marginBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* リンク A */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">リンク (A)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色</label>
                  <input
                    type="text"
                    value={customStyles.a.color}
                    onChange={(e) => setCustomStyles({...customStyles, a: {...customStyles.a, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: #02D0F0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">ホバー色</label>
                  <input
                    type="text"
                    value={customStyles.a.hoverColor}
                    onChange={(e) => setCustomStyles({...customStyles, a: {...customStyles.a, hoverColor: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: #07DBCA"
                  />
                </div>
              </div>
            </div>

            {/* リスト UL */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">箇条書きリスト (UL)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">左余白</label>
                  <input
                    type="text"
                    value={customStyles.ul.marginLeft}
                    onChange={(e) => setCustomStyles({...customStyles, ul: {...customStyles.ul, marginLeft: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1.5rem"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">リストスタイル</label>
                  <input
                    type="text"
                    value={customStyles.ul.listStyle}
                    onChange={(e) => setCustomStyles({...customStyles, ul: {...customStyles.ul, listStyle: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: disc"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">行の高さ</label>
                  <input
                    type="text"
                    value={customStyles.ul.lineHeight}
                    onChange={(e) => setCustomStyles({...customStyles, ul: {...customStyles.ul, lineHeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 1.6"
                  />
                </div>
              </div>
            </div>

            {/* リスト項目 LI */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">リスト項目 (LI)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントサイズ</label>
                  <input
                    type="text"
                    value={customStyles.li.fontSize}
                    onChange={(e) => setCustomStyles({...customStyles, li: {...customStyles.li, fontSize: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色</label>
                  <input
                    type="text"
                    value={customStyles.li.color}
                    onChange={(e) => setCustomStyles({...customStyles, li: {...customStyles.li, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">行の高さ</label>
                  <input
                    type="text"
                    value={customStyles.li.lineHeight}
                    onChange={(e) => setCustomStyles({...customStyles, li: {...customStyles.li, lineHeight: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下余白</label>
                  <input
                    type="text"
                    value={customStyles.li.marginBottom}
                    onChange={(e) => setCustomStyles({...customStyles, li: {...customStyles.li, marginBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* 画像 IMG */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">画像 (IMG)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">最大幅</label>
                  <input
                    type="text"
                    value={customStyles.img.maxWidth}
                    onChange={(e) => setCustomStyles({...customStyles, img: {...customStyles.img, maxWidth: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 100%"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">高さ</label>
                  <input
                    type="text"
                    value={customStyles.img.height}
                    onChange={(e) => setCustomStyles({...customStyles, img: {...customStyles.img, height: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: auto"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">角丸</label>
                  <input
                    type="text"
                    value={customStyles.img.borderRadius}
                    onChange={(e) => setCustomStyles({...customStyles, img: {...customStyles.img, borderRadius: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 8px"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">シャドウ</label>
                  <input
                    type="text"
                    value={customStyles.img.boxShadow}
                    onChange={(e) => setCustomStyles({...customStyles, img: {...customStyles.img, boxShadow: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 0 2px 4px rgba(0,0,0,0.05)"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下余白</label>
                  <input
                    type="text"
                    value={customStyles.img.marginBottom}
                    onChange={(e) => setCustomStyles({...customStyles, img: {...customStyles.img, marginBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* 引用 BLOCKQUOTE */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">引用 (BLOCKQUOTE)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">左ボーダー</label>
                  <input
                    type="text"
                    value={customStyles.blockquote.borderLeft}
                    onChange={(e) => setCustomStyles({...customStyles, blockquote: {...customStyles.blockquote, borderLeft: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: 4px solid #02D0F0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">左パディング</label>
                  <input
                    type="text"
                    value={customStyles.blockquote.paddingLeft}
                    onChange={(e) => setCustomStyles({...customStyles, blockquote: {...customStyles.blockquote, paddingLeft: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">背景色</label>
                  <input
                    type="text"
                    value={customStyles.blockquote.backgroundColor}
                    onChange={(e) => setCustomStyles({...customStyles, blockquote: {...customStyles.blockquote, backgroundColor: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: #F6F8FA"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントサイズ</label>
                  <input
                    type="text"
                    value={customStyles.blockquote.fontSize}
                    onChange={(e) => setCustomStyles({...customStyles, blockquote: {...customStyles.blockquote, fontSize: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色</label>
                  <input
                    type="text"
                    value={customStyles.blockquote.color}
                    onChange={(e) => setCustomStyles({...customStyles, blockquote: {...customStyles.blockquote, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントスタイル</label>
                  <input
                    type="text"
                    value={customStyles.blockquote.fontStyle}
                    onChange={(e) => setCustomStyles({...customStyles, blockquote: {...customStyles.blockquote, fontStyle: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: italic"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">上余白</label>
                  <input
                    type="text"
                    value={customStyles.blockquote.marginTop}
                    onChange={(e) => setCustomStyles({...customStyles, blockquote: {...customStyles.blockquote, marginTop: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下余白</label>
                  <input
                    type="text"
                    value={customStyles.blockquote.marginBottom}
                    onChange={(e) => setCustomStyles({...customStyles, blockquote: {...customStyles.blockquote, marginBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* コード CODE */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">コード (CODE)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">背景色</label>
                  <input
                    type="text"
                    value={customStyles.code.backgroundColor}
                    onChange={(e) => setCustomStyles({...customStyles, code: {...customStyles.code, backgroundColor: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: #F6F8FA"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色</label>
                  <input
                    type="text"
                    value={customStyles.code.color}
                    onChange={(e) => setCustomStyles({...customStyles, code: {...customStyles.code, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントファミリー</label>
                  <input
                    type="text"
                    value={customStyles.code.fontFamily}
                    onChange={(e) => setCustomStyles({...customStyles, code: {...customStyles.code, fontFamily: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: Monaco, monospace"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントサイズ</label>
                  <input
                    type="text"
                    value={customStyles.code.fontSize}
                    onChange={(e) => setCustomStyles({...customStyles, code: {...customStyles.code, fontSize: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">パディング</label>
                  <input
                    type="text"
                    value={customStyles.code.padding}
                    onChange={(e) => setCustomStyles({...customStyles, code: {...customStyles.code, padding: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">角丸</label>
                  <input
                    type="text"
                    value={customStyles.code.borderRadius}
                    onChange={(e) => setCustomStyles({...customStyles, code: {...customStyles.code, borderRadius: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* コードブロック PRE */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3">コードブロック (PRE)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">背景色</label>
                  <input
                    type="text"
                    value={customStyles.pre.backgroundColor}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, backgroundColor: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">色</label>
                  <input
                    type="text"
                    value={customStyles.pre.color}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, color: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントファミリー</label>
                  <input
                    type="text"
                    value={customStyles.pre.fontFamily}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, fontFamily: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">フォントサイズ</label>
                  <input
                    type="text"
                    value={customStyles.pre.fontSize}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, fontSize: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">パディング</label>
                  <input
                    type="text"
                    value={customStyles.pre.padding}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, padding: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">角丸</label>
                  <input
                    type="text"
                    value={customStyles.pre.borderRadius}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, borderRadius: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">オーバーフロー</label>
                  <input
                    type="text"
                    value={customStyles.pre.overflow}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, overflow: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="例: auto"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">上余白</label>
                  <input
                    type="text"
                    value={customStyles.pre.marginTop}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, marginTop: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">下余白</label>
                  <input
                    type="text"
                    value={customStyles.pre.marginBottom}
                    onChange={(e) => setCustomStyles({...customStyles, pre: {...customStyles.pre, marginBottom: e.target.value}})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* スタイルプレビュー */}
            <div className="bg-gray-50 p-6 rounded-lg border-2 border-blue-200">
              <h4 className="font-medium text-gray-900 mb-4">リアルタイムプレビュー</h4>
              <style>{stylesToCSS(customStyles)}</style>
              <div
                className="article-content bg-white p-4 rounded"
                dangerouslySetInnerHTML={{
                  __html: `
                    <h1>見出し1のサンプル - グラデーション効果</h1>
                    <p>これは段落のサンプルテキストです。行の高さやフォントファミリーを確認できます。</p>
                    <h2>見出し2のサンプル - 左アクセントライン付き</h2>
                    <p>段落のサンプルテキストです。<strong>太字</strong>と<a href="#">リンク</a>を含みます。</p>
                    <h3>見出し3のサンプル - ダイヤモンド記号付き</h3>
                    <ul>
                      <li>リスト項目1 - 箇条書きのスタイルを確認</li>
                      <li>リスト項目2 - 行の高さや余白を確認</li>
                    </ul>
                    <blockquote>引用文のサンプルです。背景色と左ボーダーのスタイルを確認できます。</blockquote>
                    <p>インラインコードの例: <code>const x = 10;</code></p>
                    <pre><code>// コードブロックのサンプル
function hello() {
  console.log("Hello, World!");
}</code></pre>
                    <img src="https://via.placeholder.com/600x300" alt="サンプル画像" style="width: 100%;" />
                  `
                }}
              />
            </div>
          </div>
        )}

        {/* アクションボタン */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="whitespace-nowrap cursor-pointer w-full sm:w-auto"
          >
            キャンセル
          </Button>
          <Button type="submit" className="whitespace-nowrap cursor-pointer w-full sm:w-auto">
            <i className="ri-save-line mr-2"></i>
            {article ? '更新' : '保存'}
          </Button>
        </div>
      </form>

      {/* 概要・抜粋用の画像アップロードモーダル */}
      {/* HTMLコンテンツ用の画像挿入モーダル */}
      {showHtmlImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  <i className="ri-image-add-line mr-2"></i>
                  HTMLコンテンツ用の画像をアップロード
                </h3>
                <button
                  type="button"
                  onClick={() => setShowHtmlImageModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={htmlImageUploading}
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <i className="ri-information-line mr-1"></i>
                  画像をアップロードすると、HTMLコンテンツエディタのカーソル位置に img タグを挿入します。カーソルを置いていなければ、本文の末尾（&lt;/article&gt; があればその直前）に入ります。
                </p>
              </div>

              {/* ドラッグ&ドロップゾーン */}
              <div
                onDragEnter={handleHtmlImageDragEnter}
                onDragLeave={handleHtmlImageDragLeave}
                onDragOver={handleHtmlImageDragOver}
                onDrop={handleHtmlImageDrop}
                className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-all ${
                  htmlImageDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                } ${htmlImageUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleHtmlImageFileSelect}
                  disabled={htmlImageUploading}
                  className="hidden"
                  id="html-image-upload"
                />
                {htmlImageUploading ? (
                  <div className="flex flex-col items-center">
                    <i className="ri-loader-4-line text-5xl text-blue-600 animate-spin mb-4"></i>
                    <p className="text-base font-medium text-blue-600">
                      Cloudinaryにアップロード中...
                    </p>
                  </div>
                ) : htmlImageDragging ? (
                  <div className="flex flex-col items-center">
                    <i className="ri-download-cloud-line text-5xl text-blue-600 mb-4"></i>
                    <p className="text-base font-medium text-blue-600">
                      ここにドロップしてアップロード
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <i className="ri-upload-cloud-line text-5xl text-gray-400 mb-4"></i>
                    <p className="text-base font-medium text-gray-700 mb-2">
                      画像をドラッグ&ドロップ
                    </p>
                    <p className="text-sm text-gray-500 mb-3">
                      または
                    </p>
                    <label
                      htmlFor="html-image-upload"
                      className="px-6 py-3 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 inline-block cursor-pointer"
                    >
                      <i className="ri-folder-open-line mr-2"></i>
                      ファイルを選択
                    </label>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                JPG、PNG、GIF形式（5MB以下）。アップロードするとCloudinaryに保存されます。
              </p>

              <div className="mt-6 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowHtmlImageModal(false)}
                  disabled={htmlImageUploading}
                  className="whitespace-nowrap cursor-pointer"
                >
                  閉じる
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 概要・抜粋用の画像挿入モーダル */}
      {showExcerptImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  <i className="ri-image-add-line mr-2"></i>
                  概要・抜粋用の画像をアップロード
                </h3>
                <button
                  type="button"
                  onClick={() => setShowExcerptImageModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={excerptImageUploading}
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <i className="ri-information-line mr-1"></i>
                  画像をアップロードすると、概要・抜粋欄のカーソル位置にマークダウン記法を挿入します。本文中の写真は「HTMLコンテンツ」タブの「画像を挿入」を使ってください。
                </p>
              </div>

              {/* ドラッグ&ドロップゾーン */}
              <div
                onDragEnter={handleExcerptImageDragEnter}
                onDragLeave={handleExcerptImageDragLeave}
                onDragOver={handleExcerptImageDragOver}
                onDrop={handleExcerptImageDrop}
                className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-all ${
                  excerptImageDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                } ${excerptImageUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleExcerptImageFileSelect}
                  disabled={excerptImageUploading}
                  className="hidden"
                  id="excerpt-image-upload"
                />
                <label
                  htmlFor="excerpt-image-upload"
                  className="cursor-pointer"
                >
                  {excerptImageUploading ? (
                    <div className="flex flex-col items-center">
                      <i className="ri-loader-4-line text-5xl text-blue-600 animate-spin mb-4"></i>
                      <p className="text-base font-medium text-blue-600">
                        Cloudinaryにアップロード中...
                      </p>
                    </div>
                  ) : excerptImageDragging ? (
                    <div className="flex flex-col items-center">
                      <i className="ri-download-cloud-line text-5xl text-blue-600 mb-4"></i>
                      <p className="text-base font-medium text-blue-600">
                        ここにドロップしてアップロード
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <i className="ri-upload-cloud-line text-5xl text-gray-400 mb-4"></i>
                      <p className="text-base font-medium text-gray-700 mb-2">
                        画像をドラッグ&ドロップ
                      </p>
                      <p className="text-sm text-gray-500 mb-3">
                        または
                      </p>
                      <span className="px-6 py-3 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 inline-block">
                        <i className="ri-folder-open-line mr-2"></i>
                        ファイルを選択
                      </span>
                    </div>
                  )}
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                JPG、PNG、GIF形式（5MB以下）。アップロードするとCloudinaryに保存されます。
              </p>

              <div className="mt-6 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowExcerptImageModal(false)}
                  disabled={excerptImageUploading}
                  className="whitespace-nowrap cursor-pointer"
                >
                  閉じる
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
